import type { BetSlipState } from '@swf/core/models/sports/types/betSlip';
import {
  getIsMultipleOpportunity,
  getIsSgmOpportunity,
  getIsSingleOpportunity
} from '@swf/core/models/sports/utils/betSlip/getIsOpportunity';
import type {
  BetType,
  MultipleAvailableOpportunity,
  SingleAvailableOpportunity,
  SingleMinOddsRestrictedOpportunity
} from '@swf/generated/types/api/sports';
import type { BetDefinition, BetLeg } from '@swf/sports/types/place-bet';
import { getBetNo } from '@swf/sports/utils/getBetNo';
import { notEmpty } from '@swf/sports/utils/notEmpty';

import type { FormData } from '../types';

const buildLeg = (
  singleOpportunity: SingleAvailableOpportunity | SingleMinOddsRestrictedOpportunity,
  formValues: FormData
): BetLeg => {
  const [legCombination] = singleOpportunity.legCombinations;
  const opportunityInput = formValues[singleOpportunity.id];
  const hasStartingPriceSelected = Boolean(opportunityInput?.startingPriceSelected);

  return {
    legType: legCombination.legType,
    betRunners: [
      { runner: { ...singleOpportunity.runnerId, handicap: singleOpportunity.handicap } }
    ],
    winExpectedOdds: singleOpportunity.winRunnerOdds?.trueOdds,
    ...(hasStartingPriceSelected ? { bsp: true, guaranteedPrice: false } : {})
  };
};

const buildLegs = (
  opportunity: SingleAvailableOpportunity | MultipleAvailableOpportunity,
  singleOpportunities: (SingleAvailableOpportunity | SingleMinOddsRestrictedOpportunity)[],
  formValues: FormData
): BetLeg[] => {
  if (getIsSingleOpportunity(opportunity)) {
    return [buildLeg(opportunity, formValues)];
  }

  if (getIsSgmOpportunity(opportunity)) {
    return opportunity.legCombinations
      .map((legCombination) => {
        const opportunityRunners = legCombination.runners;

        const singleOpportunity = singleOpportunities.find((singleOpportunityRunner) =>
          opportunityRunners?.find((opportunityRunner) => {
            const singleOpportunityRunners = singleOpportunityRunner.legCombinations[0].runners;

            return singleOpportunityRunners?.find(
              (singleOpportunity) =>
                singleOpportunity.marketId === opportunityRunner.marketId &&
                singleOpportunity.selectionId === opportunityRunner.selectionId
            );
          })
        );

        if (!singleOpportunity) {
          return undefined;
        }

        return buildLeg(singleOpportunity, formValues);
      })
      .filter(notEmpty);
  }

  return singleOpportunities.map((singleOpportunity) => buildLeg(singleOpportunity, formValues));
};

interface BuildBetDefinitions {
  opportunities: SingleAvailableOpportunity[] | MultipleAvailableOpportunity[];
  singleOpportunities: (SingleAvailableOpportunity | SingleMinOddsRestrictedOpportunity)[];
  formValues: FormData;
  wallets: BetSlipState['wallets'];
  bettableSingleOpportunitiesCount?: number;
}

export const buildBetDefinitions = ({
  opportunities,
  singleOpportunities,
  formValues,
  wallets,
  bettableSingleOpportunitiesCount
}: BuildBetDefinitions) =>
  opportunities.map<BetDefinition>((opportunity, opportunityIndex) => {
    const { stakePerLineParsed, eachWaySelected } = formValues[opportunity.id] || {};
    const betNo = getBetNo(
      opportunityIndex,
      getIsMultipleOpportunity(opportunity) ? bettableSingleOpportunitiesCount ?? 0 : 0
    );
    const opportunityInput = formValues[opportunity.id];
    const [bonusCashWalletId] = (opportunityInput?.bonusCash || opportunityInput?.freeBet) ?? [];
    const extraWalletApplied = bonusCashWalletId ? wallets[bonusCashWalletId] : undefined;

    return {
      id: opportunity.id,
      stakePerLine: stakePerLineParsed || 0,
      betNo,
      betReference: opportunity.betReference,
      takeEachway: eachWaySelected,
      betType: opportunity.type as BetType,
      winExpectedOdds: opportunity.winRunnerOdds,
      legs: buildLegs(opportunity, singleOpportunities, formValues),
      wallets: extraWalletApplied ? [extraWalletApplied] : []
    };
  });
